import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

export interface PushNotification {
  id: string;
  title: string;
  message: string;
  data?: Record<string, any>;
  image?: string;
  url?: string;
  user_id?: string;
  scheduled_at?: string;
  sent_at?: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  delivery_count: number;
  open_count: number;
  click_count: number;
  error_message?: string;
  created_at: string;
}

export interface InAppNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  read_at?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  type: 'push' | 'email' | 'sms' | 'in_app';
  variables?: string[];
  is_active: boolean;
  category: 'agendamento' | 'avaliacao' | 'promocao' | 'sistema' | 'general';
  created_by: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useNotificationsDB = () => {
  const [inAppNotifications, setInAppNotifications] = useState<InAppNotification[]>([]);
  const [pushNotifications, setPushNotifications] = useState<PushNotification[]>([]);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  // Buscar notificações in-app do usuário
  const fetchInAppNotifications = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('in_app_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const notifications = (data || []) as InAppNotification[];
      setInAppNotifications(notifications);
      setUnreadCount(notifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Erro ao buscar notificações in-app:', error);
    }
  }, [user]);

  // Buscar templates de notificação
  const fetchTemplates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setTemplates((data || []) as NotificationTemplate[]);
    } catch (error) {
      console.error('Erro ao buscar templates:', error);
    }
  }, []);

  // Marcar notificação como lida
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('in_app_notifications')
        .update({
          read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Atualizar estado local
      setInAppNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true, read_at: new Date().toISOString() }
            : notification
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  }, [user]);

  // Marcar todas como lidas
  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('in_app_notifications')
        .update({
          read: true,
          read_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;

      // Atualizar estado local
      setInAppNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          read: true,
          read_at: new Date().toISOString()
        }))
      );

      setUnreadCount(0);
      toast.success('Todas as notificações foram marcadas como lidas');
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
      toast.error('Erro ao marcar notificações como lidas');
    }
  }, [user]);

  // Criar notificação in-app
  const createInAppNotification = useCallback(async (
    userId: string,
    title: string,
    message: string,
    type: InAppNotification['type'] = 'info',
    metadata?: Record<string, any>
  ) => {
    try {
      const { data, error } = await supabase
        .from('in_app_notifications')
        .insert({
          user_id: userId,
          title,
          message,
          type,
          metadata: metadata || {},
          read: false
        })
        .select()
        .single();

      if (error) throw error;

      // Se for para o usuário atual, atualizar estado local
      if (userId === user?.id) {
        setInAppNotifications(prev => [data, ...prev]);
        setUnreadCount(prev => prev + 1);
      }

      return data;
    } catch (error) {
      console.error('Erro ao criar notificação in-app:', error);
      throw error;
    }
  }, [user]);

  // Criar notificação push usando template
  const createPushFromTemplate = useCallback(async (
    templateId: string,
    userId?: string,
    variables?: Record<string, string>
  ) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        throw new Error('Template não encontrado');
      }

      let content = template.content;
      let subject = template.subject;

      // Substituir variáveis no template
      if (variables && template.variables) {
        template.variables.forEach(variable => {
          const value = variables[variable] || '';
          content = content.replace(new RegExp(`{{${variable}}}`, 'g'), value);
          subject = subject.replace(new RegExp(`{{${variable}}}`, 'g'), value);
        });
      }

      const { data, error } = await supabase
        .from('push_notifications')
        .insert({
          title: subject,
          message: content,
          user_id: userId,
          status: 'pending',
          delivery_count: 0,
          open_count: 0,
          click_count: 0
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao criar push notification:', error);
      throw error;
    }
  }, [templates]);

  // Deletar notificação in-app
  const deleteInAppNotification = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('in_app_notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Atualizar estado local
      setInAppNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId);
        const newNotifications = prev.filter(n => n.id !== notificationId);
        
        if (notification && !notification.read) {
          setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }
        
        return newNotifications;
      });

      toast.success('Notificação removida');
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
      toast.error('Erro ao remover notificação');
    }
  }, [user]);

  // Subscription para notificações em tempo real
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('user_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'in_app_notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const newNotification = payload.new as InAppNotification;
          setInAppNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);
          
          // Mostrar toast para nova notificação
          toast.info(newNotification.title, {
            description: newNotification.message
          });
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  // Carregar dados iniciais
  useEffect(() => {
    if (user) {
      fetchInAppNotifications();
      fetchTemplates();
    }
  }, [user, fetchInAppNotifications, fetchTemplates]);

  return {
    // Estados
    inAppNotifications,
    pushNotifications,
    templates,
    loading,
    unreadCount,
    
    // Ações
    fetchInAppNotifications,
    fetchTemplates,
    markAsRead,
    markAllAsRead,
    createInAppNotification,
    createPushFromTemplate,
    deleteInAppNotification,
    
    // Helpers
    getUnreadNotifications: () => inAppNotifications.filter(n => !n.read),
    getNotificationsByType: (type: InAppNotification['type']) => 
      inAppNotifications.filter(n => n.type === type),
    getTemplatesByCategory: (category: NotificationTemplate['category']) =>
      templates.filter(t => t.category === category)
  };
};
